<template>
  <section class="m-provider-page" data-t="m-provider-page">
    <div class="banners">
      <StMBanners
        :is-loading="isLoadingMobileMenuBanners"
        :items="banners"
        size="l"
      />
    </div>
    <div ref="contentElement" class="content" :class="{ scrolled: isScrolled }">
      <header class="title-group">
        <div class="header-group">
          <h3 class="provider-title" data-t="provider-title-9utr">
            {{ providerName }}
          </h3>
          <StCounter
            v-if="games"
            bg-color="ghost"
            color="white"
            class="counter"
            data-t="games-counter-ap1o"
          >
            {{ games }}
          </StCounter>
          <MSelectSorting v-model="sort" is-native class="sorting" />
        </div>
      </header>
      <div class="top-bar">
        <h4 class="top-bar-title">
          {{ t('casino.gamesList') }}
        </h4>
        <div class="toggle-wrapper">
          <span class="toggle-label">{{ t('casino.showUnavailable') }}</span>
          <StToggle
            v-model="forbiddenByCountryOnly"
            class="toggle"
            :disabled="forbiddenByCountryDisabled"
          />
        </div>
      </div>
      <div class="provider-list">
        <MGamesInfiniteList
          :request-order-by="orderBy"
          :request-params="paramsValue"
          hide-title
          @games-counter="setGamesCounter"
        />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import MGamesInfiniteList from '@st/casino/components/GamesInfiniteList/MGamesInfiniteList.vue'

interface Props {
  providerCode: string
  providerName: string
}

const { providerCode, providerName } = defineProps<Props>()

const { banners, isLoading: isLoadingMobileMenuBanners } =
  useBanners('mobileMenu')

const { t } = useI18n()

const forbiddenByCountryOnly = ref<boolean | undefined>(undefined)
const forbiddenByCountryDisabled = ref<boolean>(false)
const paramsValue = computed(() => ({
  providerCode,
  allowedByCountryOnly: !forbiddenByCountryOnly.value,
}))

const contentElement = useTemplateRef<HTMLDivElement>('contentElement')
const offsetTop = computed(() => contentElement.value?.offsetTop ?? 22)

const { isScrolled } = useScrolled({ offsetY: offsetTop })

const games = ref()
function setGamesCounter(val: number) {
  games.value = val
  if (val === 0) {
    forbiddenByCountryOnly.value = true
    forbiddenByCountryDisabled.value = true
  }
}

const { sort, orderBy } = useSelectSorting()
</script>

<style scoped>
.m-provider-page {
  position: relative;
}

.content {
  position: relative;

  &::before {
    content: '';

    position: absolute;
    z-index: -2;
    top: 0;
    right: 0;
    left: 0;

    overflow: hidden;

    width: 100%;
    height: 240px;

    background-color: var(--background-primary);
    border-radius: var(--border-radius-200) var(--border-radius-200) 0 0;

    mask-image: linear-gradient(180deg, var(--background-primary), transparent);
  }
}

.title-group {
  position: sticky;
  z-index: 9;
  top: 48px;
}

.banners {
  margin: var(--spacing-150) 0 var(--spacing-200);
}

.header-group {
  position: sticky;
  top: 48px;

  display: flex;
  gap: var(--spacing-100);
  align-items: center;
  justify-content: space-between;

  padding: var(--spacing-150) var(--spacing-200) var(--spacing-100)
    var(--spacing-200);

  border-bottom: var(--border-width-light) solid var(--border-primary);
  border-radius: var(--border-radius-200) var(--border-radius-200) 0 0;
}

.counter {
  margin-right: auto;
}

.block-wrapper {
  width: 100%;
}

.provider-title {
  margin: 0;
  font: var(--mobile-title-2-semibold);
}

.toggle-wrapper {
  display: flex;
  gap: var(--spacing-075);
  align-items: center;
}

.toggle-label {
  font: var(--mobile-text-content-regular);
  color: var(--text-secondary);
  white-space: nowrap;
}

.toggle {
  width: 45px;
}

.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-100) var(--spacing-200);
}

.top-bar-title {
  margin: 0;
  font: var(--mobile-text-semibold);
}

.scrolled {
  .title-group {
    background-color: var(--background-base);
    box-shadow: 0 12px 40px -12px #000000;
  }

  .header-group {
    background-color: var(--background-primary);
  }

  .top-bar {
    background-color: var(--background-primary);
  }
}

.sorting {
  width: auto;
  min-width: 110px;
  max-width: 100%;
}
</style>
